import { z } from "zod";
import { InvoicesStatusOptions, type InvoicesAbstractTypeOptions } from "@/lib/core/pb-types";
import { LABEL_NAMES, type LabelName } from "./labels";
import { VAT_MODES } from "./vat-modes";

export type AbstractType = InvoicesAbstractTypeOptions;
export type InvoiceStatus = InvoicesStatusOptions;
/** Invoices and quotes have their own lifecycles (both start as draft, then sent). */
export const INVOICE_STATUSES = ["draft", "open", "sent", "paid", "overdue", "cancelled"] as const;
export const QUOTE_STATUSES = ["draft", "sent", "accepted", "declined", "expired"] as const;
export const ALL_STATUSES = Object.values(InvoicesStatusOptions);
export const statusesFor = (kind: "invoice" | "quote"): readonly InvoiceStatus[] =>
  kind === "quote" ? QUOTE_STATUSES : INVOICE_STATUSES;

const text = z.string().trim().max(10_000).default("");
const percentage = z.number().min(0).max(100);
// Calendar dates are edited as YYYY-MM-DD; empty means "not set".
const dateString = z.union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]);

export const invoiceItemSchema = z.object({
  title: z.string().max(500).default(""),
  description: z.string().max(2_000).default(""),
  quantity: z.number().min(0).default(1),
  /** Printed after the quantity ("8 h"). */
  unit: z.string().trim().max(20).default(""),
  unitPrice: z.number().default(0),
  /** The line's VAT rate; null = the invoice's standard rate (`tax`). */
  taxRate: percentage.nullable().default(null),
  /**
   * Exempt from VAT (not a 0% rate): charges none and names the reason, which the invoice has to
   * state (§ 14 (4) no. 8 UStG, Art. 226 no. 11 VAT Directive). Only with standard VAT.
   */
  exempt: z.boolean().default(false),
  exemptionReason: z.string().trim().max(300).default(""),
});

/**
 * The invoice's VAT treatment. Anything but "standard" charges no VAT and prints `vatNote`
 * instead (same list as the PDF service and the `vatMode` field).
 */
export { VAT_MODES, type VatMode } from "./vat-modes";
export type InvoiceItem = z.infer<typeof invoiceItemSchema>;

const labelsShape = Object.fromEntries(
  LABEL_NAMES.map((name) => [name, z.string().max(200).default("")]),
) as Record<LabelName, z.ZodDefault<z.ZodString>>;

/**
 * Every user-editable invoice field, shared by invoices, templates and the workspace default.
 * Files are uploaded separately; system fields (workspace, abstractType, totals) are never sent
 * from the editor. Validation messages are translated by the form (see invoice-editor.tsx).
 */
export const invoiceFormSchema = z.object({
  invoiceTitle: text,
  invoiceNumber: text,
  dueDateInDays: z.number().int().min(0).max(3650).default(30),
  quoteValidityDays: z.number().int().min(0).max(3650).default(30),
  // The workspace's own email text (Invoice settings; empty = built-in, lib/invoice/email-presets.ts).
  emailSubject: z.string().trim().max(300).default(""),
  emailMessage: z.string().trim().max(5_000).default(""),
  quoteEmailSubject: z.string().trim().max(300).default(""),
  quoteEmailMessage: z.string().trim().max(5_000).default(""),
  issueDate: dateString.default(""),
  dueDate: dateString.default(""),
  fromDate: dateString.default(""),
  toDate: dateString.default(""),
  customer: z.string().max(15).default(""),
  /** A payment_methods id; "" prints no payment details. */
  paymentMethod: z.string().max(15).default(""),
  status: z.enum(InvoicesStatusOptions).default("draft"),
  currency: z.string().length(3).default("EUR"),
  currencyFormat: z.string().max(10).default("en-US"),
  dateFormat: z.string().max(20).default("YYYY-MM-DD"),
  template: z.string().max(50).default("montana"),
  /** #rrggbb for designs with a colour (TEMPLATE_ACCENTS); "" = the design's own. */
  accentColor: z
    .string()
    .regex(/^(#[0-9a-f]{6})?$/)
    .default(""),
  footNoteLeft: text,
  footNoteRight: text,
  discount: percentage.default(0),
  /** The standard VAT rate: lines without their own rate use it. */
  tax: percentage.default(0),
  vatMode: z.enum(VAT_MODES).default("standard"),
  /** Printed under the totals, e.g. "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." */
  vatNote: z.string().trim().max(2_000).default(""),
  items: z.array(invoiceItemSchema).max(500).default([]),
  /** The payment note prints a QR code for the total unless this is set. */
  hidePaymentQr: z.boolean().default(false),
  ...labelsShape,
});

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;
/** Raw form state (before zod defaults/validation). */
export type InvoiceFormInput = z.input<typeof invoiceFormSchema>;

/** "2026-01-15 00:00:00.000Z" -> "2026-01-15" */
export const toFormDate = (value?: string | null) => (value ? value.slice(0, 10) : "");
